/**
 * AfCFTA Trade Checker Component
 * 
 * A multi-step widget for calculating AfCFTA trade duties and compliance.
 * File size: ~1038 lines (consider refactoring if adding new features)
 * 
 * COMPONENT STRUCTURE:
 * ├── WIDGET_TRANSLATIONS (Lines 50-210) - Multi-language support object
 * ├── useDebounce hook (Lines 215-223) - Custom debounce hook
 * ├── AfCFTATradeChecker (Lines 224-1038) - Main component
 *     ├── State & Handlers (Lines 237-448)
 *     ├── Header + Progress (Lines 449-532)
 *     ├── SMS Modal (Lines 534-655) [EXTRACTABLE: SMSFallbackModal]
 *     ├── Step 1: Input Form (Lines 660-820) [EXTRACTABLE: TradeInputForm]
 *     ├── Step 2: Results (Lines 821-970) [EXTRACTABLE: TradeResultsPanel]
 *     └── Step 3: Lead Capture (Lines 971-1020) [EXTRACTABLE: LeadCaptureForm]
 * 
 * REFACTORING NOTES:
 * - SMSFallbackModal: Can be extracted to /components/trade/SMSFallbackModal.jsx
 * - TradeResultsPanel: Can be extracted to /components/trade/TradeResultsPanel.jsx
 * - Consider using React.lazy() for Step 2/3 components for code splitting
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  ArrowRight, 
  CheckCircle2, 
  AlertCircle, 
  Globe2, 
  FileText, 
  TrendingDown, 
  Calculator,
  Package,
  Shield,
  Loader2,
  ChevronDown,
  X,
  ExternalLink,
  CreditCard,
  Smartphone,
  Lock,
  MessageSquare,
  Phone
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Widget-specific translations (fallback when i18n not available)
const WIDGET_TRANSLATIONS = {
  en: {
    title: "AfCFTA Trade Checker",
    subtitle: "Calculate duties & check compliance",
    origin_country: "Origin Country",
    destination_country: "Destination Country",
    product_hs_code: "Product (HS Code)",
    consignment_value: "Consignment Value (USD)",
    calculate: "Calculate Duties",
    savings_message: "Trading under AfCFTA saves you",
    mfn_rate: "Standard MFN Rate",
    afcfta_rate: "AfCFTA Preferential Rate",
    required_certificates: "Required Certificates",
    rules_of_origin: "Rules of Origin",
    generate_docs: "Generate Documents",
    new_calculation: "New Calculation",
    powered_by: "Powered by Afriqoma",
    payment_providers: "Payment Providers",
    premium_feature: "Premium Feature",
    upgrade_required: "Subscribe to access",
    sms_fallback: "Get Results via SMS",
    phone_number: "Phone Number",
    send_via_sms: "Send via SMS",
    send_via_whatsapp: "Send via WhatsApp",
    compliance_services: "Compliance Services",
  },
  fr: {
    title: "Vérificateur de Commerce AfCFTA",
    subtitle: "Calculez les droits et vérifiez la conformité",
    origin_country: "Pays d'Origine",
    destination_country: "Pays de Destination",
    product_hs_code: "Produit (Code SH)",
    consignment_value: "Valeur de l'Envoi (USD)",
    calculate: "Calculer les Droits",
    savings_message: "Le commerce sous AfCFTA vous fait économiser",
    mfn_rate: "Taux NPF Standard",
    afcfta_rate: "Taux Préférentiel AfCFTA",
    required_certificates: "Certificats Requis",
    rules_of_origin: "Règles d'Origine",
    generate_docs: "Générer les Documents",
    new_calculation: "Nouveau Calcul",
    powered_by: "Propulsé par Afriqoma",
    payment_providers: "Fournisseurs de Paiement",
    premium_feature: "Fonctionnalité Premium",
    upgrade_required: "Abonnez-vous pour accéder",
    sms_fallback: "Recevez les Résultats par SMS",
    phone_number: "Numéro de Téléphone",
    send_via_sms: "Envoyer par SMS",
    send_via_whatsapp: "Envoyer par WhatsApp",
    compliance_services: "Services de Conformité",
  },
  sw: {
    title: "Kikagua cha Biashara AfCFTA",
    subtitle: "Hesabu ushuru na kagua utiifu",
    origin_country: "Nchi ya Asili",
    destination_country: "Nchi ya Marudio",
    product_hs_code: "Bidhaa (Msimbo HS)",
    consignment_value: "Thamani ya Mzigo (USD)",
    calculate: "Hesabu Ushuru",
    savings_message: "Kufanya biashara chini ya AfCFTA kunakuokoa",
    mfn_rate: "Kiwango cha MFN",
    afcfta_rate: "Kiwango cha Upendeleo AfCFTA",
    required_certificates: "Vyeti Vinavyohitajika",
    rules_of_origin: "Sheria za Asili",
    generate_docs: "Tengeneza Nyaraka",
    new_calculation: "Hesabu Mpya",
    powered_by: "Inaendeshwa na Afriqoma",
    payment_providers: "Watoa Malipo",
    premium_feature: "Kipengele cha Premium",
    upgrade_required: "Jisajili kupata ufikiaji",
    sms_fallback: "Pata Matokeo kupitia SMS",
    phone_number: "Nambari ya Simu",
    send_via_sms: "Tuma kupitia SMS",
    send_via_whatsapp: "Tuma kupitia WhatsApp",
    compliance_services: "Huduma za Utiifu",
  },
  ar: {
    title: "مدقق تجارة AfCFTA",
    subtitle: "احسب الرسوم وتحقق من الامتثال",
    origin_country: "بلد المنشأ",
    destination_country: "بلد الوجهة",
    product_hs_code: "المنتج (رمز النظام المنسق)",
    consignment_value: "قيمة الشحنة (دولار أمريكي)",
    calculate: "احسب الرسوم",
    savings_message: "التجارة تحت AfCFTA توفر لك",
    mfn_rate: "معدل الدولة الأولى بالرعاية",
    afcfta_rate: "معدل AfCFTA التفضيلي",
    required_certificates: "الشهادات المطلوبة",
    rules_of_origin: "قواعد المنشأ",
    generate_docs: "إنشاء المستندات",
    new_calculation: "حساب جديد",
    powered_by: "مدعوم من Afriqoma",
    payment_providers: "مزودي الدفع",
    premium_feature: "ميزة متميزة",
    upgrade_required: "اشترك للوصول",
    sms_fallback: "احصل على النتائج عبر SMS",
    phone_number: "رقم الهاتف",
    send_via_sms: "إرسال عبر SMS",
    send_via_whatsapp: "إرسال عبر واتساب",
    compliance_services: "خدمات الامتثال",
  },
  pt: {
    title: "Verificador de Comércio AfCFTA",
    subtitle: "Calcule direitos e verifique conformidade",
    origin_country: "País de Origem",
    destination_country: "País de Destino",
    product_hs_code: "Produto (Código SH)",
    consignment_value: "Valor da Remessa (USD)",
    calculate: "Calcular Direitos",
    savings_message: "Negociar sob AfCFTA economiza",
    mfn_rate: "Taxa NMF Padrão",
    afcfta_rate: "Taxa Preferencial AfCFTA",
    required_certificates: "Certificados Necessários",
    rules_of_origin: "Regras de Origem",
    generate_docs: "Gerar Documentos",
    new_calculation: "Novo Cálculo",
    powered_by: "Desenvolvido por Afriqoma",
    payment_providers: "Provedores de Pagamento",
    premium_feature: "Recurso Premium",
    upgrade_required: "Assine para acessar",
    sms_fallback: "Receba Resultados via SMS",
    phone_number: "Número de Telefone",
    send_via_sms: "Enviar via SMS",
    send_via_whatsapp: "Enviar via WhatsApp",
    compliance_services: "Serviços de Conformidade",
  },
  zh: {
    title: "AfCFTA贸易合规检查器",
    subtitle: "计算关税并检查合规性",
    origin_country: "原产国",
    destination_country: "目的国",
    product_hs_code: "产品（HS编码）",
    consignment_value: "货物价值（美元）",
    calculate: "计算关税",
    savings_message: "在AfCFTA下交易为您节省",
    mfn_rate: "标准最惠国税率",
    afcfta_rate: "AfCFTA优惠税率",
    required_certificates: "所需证书",
    rules_of_origin: "原产地规则",
    generate_docs: "生成文件",
    new_calculation: "新计算",
    powered_by: "由Afriqoma提供支持",
    payment_providers: "支付提供商",
    premium_feature: "高级功能",
    upgrade_required: "订阅以访问",
    sms_fallback: "通过短信获取结果",
    phone_number: "电话号码",
    send_via_sms: "通过短信发送",
    send_via_whatsapp: "通过WhatsApp发送",
    compliance_services: "合规服务",
  },
};

// Language selector for widget
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'sw', name: 'Kiswahili', flag: '🇰🇪' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
];

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const AfCFTATradeChecker = ({ 
  embedded = false, 
  partnerId = 'afriqoma',
  theme = 'light',
  primaryColor = '#1e3a5f',
  defaultOrigin = '',
  defaultLanguage = 'en',
  showPaymentProviders = true,
  showSMSFallback = true,
  isPremiumUser = false,
  authToken = null
}) => {
  // i18n hook (fallback to widget translations if not available)
  const { i18n } = useTranslation();
  const [widgetLang, setWidgetLang] = useState(defaultLanguage);
  
  // Get translation text
  const getText = useCallback((key) => {
    const translations = WIDGET_TRANSLATIONS[widgetLang] || WIDGET_TRANSLATIONS['en'];
    return translations[key] || key;
  }, [widgetLang]);
  
  // State
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [hsSearchResults, setHsSearchResults] = useState([]);
  const [hsSearchLoading, setHsSearchLoading] = useState(false);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  
  // Payment providers state
  const [paymentProviders, setPaymentProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  
  // SMS fallback state
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsMethod, setSmsMethod] = useState('whatsapp');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  
  // Form data
  const [originCountry, setOriginCountry] = useState(defaultOrigin);
  const [destCountry, setDestCountry] = useState('');
  const [hsCode, setHsCode] = useState('');
  const [hsDescription, setHsDescription] = useState('');
  const [hsQuery, setHsQuery] = useState('');
  const [consignmentValue, setConsignmentValue] = useState('');
  
  // Results
  const [tariffResult, setTariffResult] = useState(null);
  const [complianceResult, setComplianceResult] = useState(null);
  
  const debouncedHsQuery = useDebounce(hsQuery, 300);
  
  // Theme styles
  const styles = useMemo(() => ({
    container: theme === 'dark' 
      ? 'bg-gray-900 text-white' 
      : 'bg-white text-gray-900',
    card: theme === 'dark'
      ? 'bg-gray-800 border-gray-700'
      : 'bg-gray-50 border-gray-200',
    input: theme === 'dark'
      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    primary: primaryColor,
  }), [theme, primaryColor]);

  // Fetch countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/v1/trade/countries`);
        setCountries(res.data.countries || []);
      } catch (error) {
        // Countries fetch failed - log in dev, use empty list
        if (process.env.NODE_ENV === 'development') {
          console.log('[TradeChecker] Countries fetch failed:', error.message);
        }
      }
    };
    fetchCountries();
  }, []);

  // Search HS codes
  useEffect(() => {
    if (debouncedHsQuery.length < 2) {
      setHsSearchResults([]);
      return;
    }
    
    const searchHS = async () => {
      setHsSearchLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/v1/trade/hs-codes/search`, {
          params: { query: debouncedHsQuery, limit: 10 }
        });
        setHsSearchResults(res.data.results || []);
      } catch (error) {
        // HS code search failed - log in dev
        if (process.env.NODE_ENV === 'development') {
          console.log('[TradeChecker] HS code search failed:', error.message);
        }
      } finally {
        setHsSearchLoading(false);
      }
    };
    searchHS();
  }, [debouncedHsQuery]);

  const selectHsCode = useCallback((result) => {
    setHsCode(result.hs_code);
    setHsDescription(result.description);
    setHsQuery(result.description);
    setHsSearchResults([]);
  }, []);

  const calculateTariffs = async () => {
    if (!originCountry || !destCountry || !hsCode || !consignmentValue) {
      setError('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const [tariffRes, complianceRes] = await Promise.all([
        axios.post(`${API_URL}/api/v1/trade/calculate-tariffs`, {
          origin_country: originCountry,
          destination_country: destCountry,
          hs_code: hsCode,
          consignment_value_usd: parseFloat(consignmentValue)
        }),
        axios.post(`${API_URL}/api/v1/trade/compliance-check`, {
          origin_country: originCountry,
          destination_country: destCountry,
          hs_code: hsCode,
          consignment_value_usd: parseFloat(consignmentValue)
        })
      ]);
      
      setTariffResult(tariffRes.data);
      setComplianceResult(complianceRes.data);
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Calculation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDocs = async () => {
    setStep(3);
  };

  const resetChecker = () => {
    setStep(1);
    setTariffResult(null);
    setComplianceResult(null);
    setPaymentProviders([]);
    setError('');
    setSmsResult(null);
  };

  // Fetch payment providers for the trade route
  const fetchPaymentProviders = useCallback(async () => {
    if (!originCountry || !destCountry || !showPaymentProviders) return;
    
    setLoadingProviders(true);
    try {
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const res = await axios.post(
        `${API_URL}/api/v1/trade/payment-providers/recommend`,
        {
          origin_country: originCountry,
          destination_country: destCountry,
          amount_usd: parseFloat(consignmentValue) || 1000,
        },
        { headers }
      );
      setPaymentProviders(res.data.providers || []);
    } catch (error) {
      // Payment providers fetch failed - non-critical, log in dev
      if (process.env.NODE_ENV === 'development') {
        console.log('[TradeChecker] Payment providers fetch failed:', error.message);
      }
    } finally {
      setLoadingProviders(false);
    }
  }, [originCountry, destCountry, consignmentValue, authToken, showPaymentProviders]);

  // Fetch providers after calculation
  useEffect(() => {
    if (step === 2 && tariffResult) {
      fetchPaymentProviders();
    }
  }, [step, tariffResult, fetchPaymentProviders]);

  // Handle SMS fallback request
  const handleSMSFallback = async () => {
    if (!smsPhone || smsPhone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setSmsSending(true);
    setError('');
    
    try {
      const res = await axios.post(`${API_URL}/api/v1/trade/sms-fallback`, {
        phone: smsPhone,
        origin_country: originCountry,
        destination_country: destCountry,
        hs_code: hsCode,
        consignment_value_usd: parseFloat(consignmentValue),
        language: widgetLang,
        delivery_method: smsMethod,
        partner_id: partnerId,
      });
      
      setSmsResult(res.data);
      setShowSMSModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send SMS. Please try again.');
    } finally {
      setSmsSending(false);
    }
  };

  // Change language
  const changeLanguage = (langCode) => {
    setWidgetLang(langCode);
    setShowLanguageSelector(false);
    // Also update i18n if available
    if (i18n && i18n.changeLanguage) {
      i18n.changeLanguage(langCode);
    }
  };

  const containerClass = embedded 
    ? `w-full ${styles.container}` 
    : `max-w-2xl mx-auto rounded-2xl shadow-2xl overflow-hidden ${styles.container}`;

  return (
    <div className={containerClass} data-testid="afcfta-checker">
      {/* Header */}
      <div 
        className="p-6 text-white relative"
        style={{ background: `linear-gradient(135deg, ${styles.primary} 0%, ${styles.primary}dd 100%)` }}
      >
        {/* Language Selector */}
        <div className="absolute top-4 right-4">
          <button
            onClick={() => setShowLanguageSelector(!showLanguageSelector)}
            className="flex items-center gap-1 px-2 py-1 rounded bg-white/20 hover:bg-white/30 transition-colors text-sm"
            data-testid="language-selector"
          >
            <Globe2 className="w-4 h-4" />
            {LANGUAGES.find(l => l.code === widgetLang)?.flag || '🌍'}
            <ChevronDown className="w-3 h-3" />
          </button>
          
          {showLanguageSelector && (
            <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border overflow-hidden z-20 min-w-[140px]">
              {LANGUAGES.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2 ${
                    widgetLang === lang.code ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span>{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-3 mb-2">
          <Globe2 className="w-8 h-8" />
          <div>
            <h2 className="text-xl font-bold">{getText('title')}</h2>
            <p className="text-sm opacity-80">{getText('subtitle')}</p>
          </div>
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center gap-2 mt-4">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div 
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  step >= s ? 'bg-white text-blue-900' : 'bg-white/20 text-white'
                }`}
              >
                {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
              </div>
              {s < 3 && (
                <div className={`w-12 h-1 mx-1 rounded ${step > s ? 'bg-white' : 'bg-white/20'}`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* SMS Fallback Modal */}
      {showSMSModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowSMSModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                {getText('sms_fallback')}
              </h3>
              <button 
                onClick={() => setShowSMSModal(false)} 
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="close-sms-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{getText('phone_number')}</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    value={smsPhone}
                    onChange={(e) => setSmsPhone(e.target.value)}
                    placeholder="+254712345678"
                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    data-testid="sms-phone-input"
                  />
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setSmsMethod('whatsapp')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    smsMethod === 'whatsapp' 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  WhatsApp
                </button>
                <button
                  onClick={() => setSmsMethod('sms')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                    smsMethod === 'sms' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  SMS
                </button>
              </div>
              
              <button
                onClick={handleSMSFallback}
                disabled={smsSending || !smsPhone}
                className="w-full py-3 rounded-lg bg-green-600 text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-green-700 transition-colors"
                data-testid="send-sms-btn"
              >
                {smsSending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    {smsMethod === 'whatsapp' ? getText('send_via_whatsapp') : getText('send_via_sms')}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* SMS Result Toast */}
      {smsResult && (
        <div className="mx-6 mt-4 p-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2 text-green-800 text-sm">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <span>{smsResult.message}</span>
          <button onClick={() => setSmsResult(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          {/* Step 1: Input Form */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Origin & Destination */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{getText('origin_country')}</label>
                  <select
                    value={originCountry}
                    onChange={(e) => setOriginCountry(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${styles.input} focus:ring-2 focus:ring-blue-500`}
                    data-testid="origin-country"
                  >
                    <option value="">Select origin</option>
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{getText('destination_country')}</label>
                  <select
                    value={destCountry}
                    onChange={(e) => setDestCountry(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${styles.input} focus:ring-2 focus:ring-blue-500`}
                    data-testid="dest-country"
                  >
                    <option value="">Select destination</option>
                    {countries.map(c => (
                      <option key={c.code} value={c.code}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* HS Code Search */}
              <div className="relative">
                <label className="block text-sm font-medium mb-1">{getText('product_hs_code')}</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={hsQuery}
                    onChange={(e) => {
                      setHsQuery(e.target.value);
                      setHsCode('');
                      setHsDescription('');
                    }}
                    placeholder="Search by product name or HS code (e.g., Coffee, 0901)"
                    className={`w-full pl-10 pr-4 py-2 rounded-lg border ${styles.input} focus:ring-2 focus:ring-blue-500`}
                    data-testid="hs-search"
                  />
                  {hsSearchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-gray-400" />
                  )}
                </div>
                
                {/* HS Search Results Dropdown */}
                {hsSearchResults.length > 0 && !hsCode && (
                  <div className={`absolute z-10 w-full mt-1 rounded-lg border shadow-lg max-h-48 overflow-y-auto ${styles.card}`}>
                    {hsSearchResults.map((result) => (
                      <button
                        key={result.hs_code}
                        onClick={() => selectHsCode(result)}
                        className="w-full px-4 py-2 text-left hover:bg-blue-50 dark:hover:bg-gray-700 flex items-center justify-between"
                      >
                        <div>
                          <span className="font-mono text-sm text-blue-600 dark:text-blue-400">{result.hs_code}</span>
                          <span className="mx-2">-</span>
                          <span className="text-sm">{result.description}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          result.category === 'A' ? 'bg-green-100 text-green-700' :
                          result.category === 'B' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          Cat {result.category}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Selected HS Code */}
                {hsCode && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="font-mono">{hsCode}</span>
                    <span className="text-gray-500">- {hsDescription}</span>
                    <button onClick={() => { setHsCode(''); setHsQuery(''); }} className="ml-auto text-red-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Consignment Value */}
              <div>
                <label className="block text-sm font-medium mb-1">{getText('consignment_value')}</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="number"
                    value={consignmentValue}
                    onChange={(e) => setConsignmentValue(e.target.value)}
                    placeholder="10,000"
                    min="0"
                    step="100"
                    className={`w-full pl-8 pr-4 py-2 rounded-lg border ${styles.input} focus:ring-2 focus:ring-blue-500`}
                    data-testid="consignment-value"
                  />
                </div>
              </div>

              {/* SMS Fallback Button - for areas with limited connectivity */}
              {showSMSFallback && originCountry && destCountry && hsCode && consignmentValue && (
                <button
                  onClick={() => setShowSMSModal(true)}
                  className="w-full py-2 rounded-lg border border-gray-300 text-gray-600 text-sm flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                  data-testid="sms-fallback-btn"
                >
                  <Smartphone className="w-4 h-4" />
                  {getText('sms_fallback')}
                </button>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Calculate Button */}
              <button
                onClick={calculateTariffs}
                disabled={loading || !originCountry || !destCountry || !hsCode || !consignmentValue}
                className="w-full py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
                style={{ backgroundColor: styles.primary }}
                data-testid="calculate-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Calculator className="w-5 h-5" />
                    {getText('calculate')}
                  </>
                )}
              </button>
            </motion.div>
          )}

          {/* Step 2: Results */}
          {step === 2 && tariffResult && complianceResult && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              {/* Savings Highlight */}
              <div 
                className="p-6 rounded-xl text-white text-center"
                style={{ background: `linear-gradient(135deg, #059669 0%, #10b981 100%)` }}
              >
                <TrendingDown className="w-10 h-10 mx-auto mb-2" />
                <p className="text-sm opacity-80">{getText('savings_message')}</p>
                <p className="text-4xl font-bold mt-1">
                  ${tariffResult.estimated_duties_saved_usd.toLocaleString()}
                </p>
                <p className="text-sm mt-1">
                  {tariffResult.currency_localized} • {tariffResult.savings_percentage}% reduction
                </p>
              </div>

              {/* Tariff Comparison */}
              <div className={`p-4 rounded-xl border ${styles.card}`}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Package className="w-5 h-5" style={{ color: styles.primary }} />
                  Tariff Comparison
                </h3>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Product:</span>
                    <span className="font-medium">{tariffResult.hs_description}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">HS Code:</span>
                    <span className="font-mono">{tariffResult.hs_code}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Route:</span>
                    <span>{tariffResult.origin_country_name} → {tariffResult.destination_country_name}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between">
                    <span className="text-gray-500">Standard MFN Rate:</span>
                    <span className="text-red-500 line-through">{tariffResult.standard_mfn_rate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">AfCFTA Rate:</span>
                    <span className="text-green-600 font-semibold">{tariffResult.afcfta_preferential_rate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      tariffResult.afcfta_category === 'A' ? 'bg-green-100 text-green-700' :
                      tariffResult.afcfta_category === 'B' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {tariffResult.afcfta_status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Rules of Origin */}
              <div className={`p-4 rounded-xl border ${styles.card}`}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <Shield className="w-5 h-5" style={{ color: styles.primary }} />
                  {getText('rules_of_origin')}
                </h3>
                
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                  {complianceResult.qualifying_criteria}
                </p>
                
                {complianceResult.rvc_threshold && (
                  <div className="flex items-center gap-2 text-sm bg-blue-50 dark:bg-blue-900/20 p-2 rounded-lg mb-3">
                    <AlertCircle className="w-4 h-4 text-blue-500" />
                    <span>Minimum {complianceResult.rvc_threshold}% Regional Value Content required</span>
                  </div>
                )}
              </div>

              {/* Required Certificates */}
              <div className={`p-4 rounded-xl border ${styles.card}`}>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-5 h-5" style={{ color: styles.primary }} />
                  {getText('required_certificates')}
                </h3>
                
                <div className="space-y-2">
                  {complianceResult.required_certificates.map((cert) => (
                    <div key={`cert-${cert.name}`} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 ${cert.required ? 'text-red-500' : 'text-gray-400'}`} />
                      <div>
                        <p className="font-medium">{cert.name}</p>
                        <p className="text-gray-500 text-xs">Issuer: {cert.issuer}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Providers Section - PREMIUM FEATURE */}
              {showPaymentProviders && (
                <div className={`p-4 rounded-xl border ${styles.card}`}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5" style={{ color: styles.primary }} />
                    {getText('payment_providers')}
                    {!isPremiumUser && (
                      <span className="ml-auto text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-700 flex items-center gap-1">
                        <Lock className="w-3 h-3" />
                        {getText('premium_feature')}
                      </span>
                    )}
                  </h3>
                  
                  {loadingProviders ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : paymentProviders.length > 0 ? (
                    <div className="space-y-2">
                      {paymentProviders.map((provider) => (
                        <div key={provider.id || `${provider.name}-${provider.country}`} className="flex items-center justify-between text-sm p-2 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{provider.name}</span>
                            {provider.afcfta_compliant && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-green-100 text-green-700">AfCFTA</span>
                            )}
                          </div>
                          {provider.premium_required ? (
                            <Lock className="w-4 h-4 text-gray-400" />
                          ) : (
                            <span className="text-xs text-gray-500">{provider.fees || ''}</span>
                          )}
                        </div>
                      ))}
                      
                      {!isPremiumUser && (
                        <a
                          href="/pricing"
                          className="block text-center text-sm text-blue-600 hover:underline mt-2"
                        >
                          {getText('upgrade_required')} →
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">PAPSS available for all AfCFTA countries</p>
                  )}
                </div>
              )}

              {/* CTA */}
              <button
                onClick={handleGenerateDocs}
                className="w-full py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-all hover:shadow-lg"
                style={{ backgroundColor: '#e8a54b' }}
                data-testid="generate-docs-btn"
              >
                {getText('generate_docs')}
                <ArrowRight className="w-5 h-5" />
              </button>

              <button
                onClick={resetChecker}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                ← {getText('new_calculation')}
              </button>
            </motion.div>
          )}

          {/* Step 3: Lead Capture / Signup */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="text-center space-y-4"
            >
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              
              <h3 className="text-xl font-bold">Ready to Export?</h3>
              <p className="text-gray-500">
                Complete your trade documentation with Afriqoma's automated AfCFTA compliance platform.
              </p>

              <div className={`p-4 rounded-xl ${styles.card} text-left space-y-2`}>
                <p className="font-semibold text-sm">What you'll get:</p>
                <ul className="text-sm space-y-1">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Automated Certificate of Origin generation
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Pre-shipment inspection coordination
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Customs clearance support
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Escrow payment protection
                  </li>
                </ul>
              </div>

              <a
                href={`https://afriqoma.com/register?ref=${partnerId}&hs=${hsCode}&origin=${originCountry}&dest=${destCountry}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-all hover:shadow-lg"
                style={{ backgroundColor: styles.primary }}
              >
                Continue to Afriqoma
                <ExternalLink className="w-5 h-5" />
              </a>

              <button
                onClick={resetChecker}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700"
              >
                ← Start New Calculation
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className={`px-6 py-3 text-center text-xs border-t ${styles.card}`}>
        {getText('powered_by')} <a href="https://afriqoma.com" target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline" style={{ color: styles.primary }}>Afriqoma</a> • Africa's B2B Trade Platform
      </div>
    </div>
  );
};

export default AfCFTATradeChecker;
